// accommodationUtils.js - Utility functions for processing accommodation data
import { isOfficeStaff, OFFICE_COLOUR, TENANT_COLOURS } from '@/constants/staff.js'

/**
 * Sort accommodations by physical name (numeric)
 */
export const sortByPhysicalName = (a, b) => {
    const numA = parseInt(a.physicalName.replace(/\D/g, '')) || 0
    const numB = parseInt(b.physicalName.replace(/\D/g, '')) || 0
    return numA - numB
}

/**
 * Process accommodation data for a specific rep
 * @param {Array} weekData - The week data array
 * @param {Number} repId - The rep ID to filter by
 * @returns {Object} Object containing rooms and inspections data
 */
export const processAccommodationData = (weekData, repId) => {
    // Use Maps for O(1) lookups instead of find operations
    const roomsMap = new Map()
    const inspectionsMap = new Map()

    let roomsPax = 0
    let inspectionsPax = 0

    // Single iteration through weekData
    weekData.forEach(passenger => {
        if (!passenger.allocPlaces || !Array.isArray(passenger.allocPlaces)) return

        passenger.allocPlaces.forEach(alloc => {
            const accommodation = alloc.accommodation
            if (!accommodation) return

            const subblockId = accommodation.subblock_id
            const inhabitantsCount = accommodation.subblock_inhabitants?.length || 0

            // Create accommodation object once
            const accommodationObj = {
                name: accommodation.name,
                subblock_id: subblockId,
                inhabitantsCount,
                physicalName: accommodation.subblock_physical_name || '',
            }

            // Check for room reps
            if (accommodation.subblock_room_reps?.some(rep => rep.id === repId)) {
                if (!roomsMap.has(subblockId)) {
                    roomsMap.set(subblockId, accommodationObj)
                    roomsPax += inhabitantsCount
                }
            }

            // Check for inspection reps
            if (accommodation.subblock_inspection_rep?.some(rep => rep.id === repId)) {
                if (!inspectionsMap.has(subblockId)) {
                    inspectionsMap.set(subblockId, accommodationObj)
                    inspectionsPax += inhabitantsCount
                }
            }
        })
    })

    // Convert maps to sorted arrays
    const roomsArray = Array.from(roomsMap.values()).sort(sortByPhysicalName)
    const inspectionsArray = Array.from(inspectionsMap.values()).sort(sortByPhysicalName)

    return {
        rooms: {
            pax: roomsPax,
            roomCount: roomsArray.length,
            rooms: roomsArray
        },
        inspections: {
            pax: inspectionsPax,
            inspectionCount: inspectionsArray.length,
            inspections: inspectionsArray
        }
    }
}

/**
 * Get rep color based on staff type and tenant: office staff in their own
 * colour, otherwise the tenant's (nuco, wasteland, whiteroad), else transparent.
 */
export const getRepColor = (rep) => {
    if (!rep) return 'transparent'
    if (isOfficeStaff(rep)) return OFFICE_COLOUR
    return TENANT_COLOURS[rep.tenant] || 'transparent'
}
/**
 * Stay information (GLOSSARY): the supplier's instructions for a room, in the
 * order the accommodation card shows them. `field` is the GBS accommodation
 * field (HTML allowed), `key` the __DICT__.accommodation label.
 */
export const STAY_INFORMATION_FIELDS = [
    { field: 'door_code', key: 'doorCode', fallback: 'Door code' },
    { field: 'late_arrival_instructions', key: 'lateArrival', fallback: 'Late arrival' },
    { field: 'key_return_instructions', key: 'keyReturn', fallback: 'Key return' },
    { field: 'linen_return_instructions', key: 'linenReturn', fallback: 'Linen return' },
    { field: 'cleaning_instructions', key: 'cleaning', fallback: 'Cleaning' },
    { field: 'damage_deposit_instructions', key: 'damageDeposit', fallback: 'Damage deposit' }
]

/**
 * Behaviour warnings (GLOSSARY) for a room: every distinct non-empty
 * commsStatus across its guests' copies of the accommodation. GBS records it
 * on the room, but each guest carries a copy, so one that differs still shows.
 * @param {Array<object|null|undefined>} accommodations
 * @returns {string[]}
 */
export const behaviourWarnings = (accommodations) => {
    const seen = new Set()
    for (const accommodation of accommodations || []) {
        const status = accommodation?.commsStatus
        if (typeof status === 'string' && status.trim()) seen.add(status.trim())
    }
    return [...seen]
}
